import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { UserPlus, Mail, Trash2, Users, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { useUser } from '../context/UserContext';
import { teamService } from '../services/teamService';
import AddTeamMemberModal from './team/AddTeamMemberModal';
import TeamMemberDetail from './team/TeamMemberDetail';
import ModalDialog from './common/ModalDialog';

export default function TeamView() {
  const { activeJiraAccount } = useUser();
  const accountId = activeJiraAccount?.id ?? null;

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberToRemove, setMemberToRemove] = useState(null);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await teamService.getMembers();
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Failed to load team');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reload (and drop any open detail) whenever the active account changes.
  useEffect(() => {
    setSelectedMember(null);
    if (accountId) loadMembers();
    else setMembers([]);
  }, [loadMembers, accountId]);

  const existingAccountIds = useMemo(() => members.map((m) => m.account_id), [members]);

  const handleAdd = async (user) => {
    setAdding(true);
    try {
      const created = await teamService.addMember({
        account_id: user.account_id,
        name: user.display_name,
        email: user.email,
        avatar_url: user.avatar_url,
      });
      toast.success(`${created.name} added to the team`);
      setShowAddModal(false);
      await loadMembers();
    } catch (err) {
      toast.error(err.message || 'Failed to add team member');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (member) => {
    setRemovingId(member.id);
    try {
      await teamService.removeMember(member.id);
      toast.success(`${member.name} removed`);
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
    } catch (err) {
      toast.error(err.message || 'Failed to remove team member');
    } finally {
      setRemovingId(null);
    }
  };

  if (!accountId) {
    return (
      <div className="px-6 py-10 text-center text-sm text-gray-600 dark:text-slate-400">
        Select an active Jira account to manage your team.
      </div>
    );
  }

  if (selectedMember) {
    return (
      <div className="px-6 py-6">
        <TeamMemberDetail member={selectedMember} onBack={() => setSelectedMember(null)} />
      </div>
    );
  }

  return (
    <div className="px-6 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100">Team</h1>
          <p className="text-sm text-gray-600 dark:text-slate-400">
            Track worklogs per member across all projects.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-500 inline-flex items-center"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add team member
        </button>
      </div>

      {loading ? (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-10 text-center text-sm text-gray-500 dark:text-slate-400">
          <RefreshCw className="w-6 h-6 mx-auto mb-3 animate-spin" />
          Loading team...
        </div>
      ) : members.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-10 text-center">
          <Users className="w-10 h-10 mx-auto text-gray-400 dark:text-slate-500 mb-3" />
          <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
            No team members yet. Add a Jira user to get started.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-500 inline-flex items-center mx-auto"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add team member
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((member) => (
            <button
              key={member.id}
              onClick={() => setSelectedMember(member)}
              className="text-left bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-white/10 p-4 hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-500/50 transition-all group"
            >
              <div className="flex items-start gap-3">
                <img
                  src={member.avatar_url}
                  alt=""
                  className="size-11 rounded-full shrink-0 bg-slate-200 dark:bg-slate-700"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 dark:text-white truncate">
                    {member.name}
                  </p>
                  {member.email && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 truncate">
                      <Mail className="w-3 h-3 shrink-0" />
                      {member.email}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setMemberToRemove(member); }}
                  aria-label={`Remove ${member.name}`}
                  className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  {removingId === member.id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </button>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddTeamMemberModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAdd}
          loading={adding}
          existingAccountIds={existingAccountIds}
        />
      )}
    <ModalDialog
        open={!!memberToRemove}
        title="Remove team member"
        description={
          memberToRemove ? (
            <>Are you sure you want to remove <strong>{memberToRemove.name}</strong> from the team?</>
          ) : ''
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        loading={removingId === memberToRemove?.id}
        onConfirm={async () => {
          await handleRemove(memberToRemove);
          setMemberToRemove(null);
        }}
        onClose={() => setMemberToRemove(null)}
      />
    </div>
  );
}